import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import {
    WithSkeleton,
    WithSkeletonContext,
} from '@r1-frontend/ui-react/components/loaders/Skeleton';
import { SliderHoc } from '@r1-frontend/ui-react/components/NukaCarousel/SliderHoc';
import { BorderRadius } from '@r1-frontend/ui-react/tokens/borderRadius';

import type { IPromoItem } from '~/src/components/Promo/popups/PartnerPopup/types';
import { YANDEX } from '~/src/components/Promo/PromoContainer/constants';
import type { IProps } from '~/src/components/Promo/PromoContainer/types';
import { MemoizedPromotionalCard } from '~/src/components/PromotionalCard';
import type { IManiaCardData } from '~/src/features/promo/types';
import type { TState } from '~/src/store';
import {
    selectPromoCategory,
    selectPromoStatus,
    selectPromoStepId,
} from '~/src/store/selectors/promo';

type TManiaCard = {
    type: 'mania',
    value: IManiaCardData,
}

type TCard =
    | {
    type: 'offer',
    value: IPromoItem,
      }
    | TManiaCard;

const isManiaCard = (value: TCard): value is TManiaCard =>
    value.type === 'mania';

export const PromoContainer = (props: IProps) => {
    const {
        popupProps,
        getFunctions,
        maniaRewards,
        getManiaCardData,
        renderPopup,
        withoutArrows,
        useButtonTextForPartnerFunction,
        isLoading,
    } = props;

    const {
        getDescription,
        getButtonText,
        getStopDate,
        getImage,
        getPromoPartnerText,
        getButtonTextForPartner,
    } = getFunctions;
    const { offers, reward, sendVasGA } = popupProps;
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [cardKey, setCardKey] = useState(0);
    const selectStepId = useSelector(
        (state: TState) => (id: number) => selectPromoStepId(state, id),
    );
    const selectCategory = useSelector(
        (state: TState) => (id: number) => selectPromoCategory(state, id),
    );
    const selectStatus = useSelector(
        (state: TState) => (id: number) => selectPromoStatus(state, id),
    );

    const getLengthCards = () => {
        const maniaRewardsCardLength = 1;
        return maniaRewards?.length
            ? offers.length + maniaRewardsCardLength
            : offers.length;
    };

    const renderPopupMemoized = useMemo(() => {
        const offer = offers[cardKey];

        if (renderPopup && offer) {
            return () =>
                renderPopup({
                    ...popupProps,
                    isPopupOpen,
                    setIsPopupOpen,
                    item: offer,
                    isConnected: selectStatus(offer.id),
                    category: selectCategory(offer.id),
                    stepId: selectStepId(offer.id),
                    isYandex: offer.alias?.includes(YANDEX),
                });
        }
        return () => undefined;
    }, [
        renderPopup,
        popupProps,
        isPopupOpen,
        offers,
        cardKey,
        selectCategory,
        selectStatus,
        selectStepId,
    ]);

    const getPropsPartners = (item: IPromoItem) => {
        return { link: item['button_link'] ?? '' };
    };

    const offerCards = useMemo(() => {
        const cards: Array<TCard> = isLoading
            ? new Array<TCard>(2).fill({} as TCard)
            : [
                  ...offers.map<TCard>((value) => ({ type: 'offer', value })),
                  ...(maniaRewards?.length && reward && getManiaCardData
                      ? [
                            {
                                type: 'mania',
                                value: getManiaCardData(reward),
                            } as TCard,
                        ]
                      : []),
              ];

        return cards.map((card, key) => (
            <WithSkeleton
                key={key}
                height={400}
                width={316}
                borderRadius={BorderRadius.OuterBlockRadius}
                inline={true}
                style={{
                    lineHeight: 1,
                    display: 'block',
                }}
            >
                {!isManiaCard(card)
                    ? (
                        <MemoizedPromotionalCard
                            key={card.value.id}
                            id={card.value.id}
                            cardKey={key}
                            setIsPopupOpenOut={setIsPopupOpen}
                            setCardKey={setCardKey}
                            description={getDescription(card.value)}
                            advertiserName={card.value.advertising?.companyName}
                            advertiserErid={card.value.advertising?.erid}
                            imgUrl={getImage(card.value)}
                            imgWebp={card.value.image_widgets_webp}
                            title={card.value.title}
                            labelData={card.value.colorLabels}
                            duration={getStopDate(
                                card.value.requestStopDate,
                                card.value.closeDate,
                                selectCategory(card.value.id),
                            )}
                            promoText={getPromoPartnerText(card.value)}
                            textNotConnected={
                                useButtonTextForPartnerFunction
                                    ? getButtonTextForPartner(
                                            {
                                            button_text: card.value.button_text,
                                            exists: card.value.exists,
                                            isConnected: false,
                                            category: card.value.category,
                                        },
                                        false,
                                    )
                                    : getButtonText(card.value)
                            }
                            isConnected={selectStatus(card.value.id)}
                            sendVasGA={sendVasGA}
                            {...getPropsPartners(card.value)}
                        />
                    )
                    : (
                        <MemoizedPromotionalCard
                            {...card.value}
                            setIsPopupOpenOut={setIsPopupOpen}
                        />
                    )}
            </WithSkeleton>
        ));
    }, [
        offers,
        getButtonText,
        getButtonTextForPartner,
        getDescription,
        getImage,
        getPromoPartnerText,
        getStopDate,
        selectCategory,
        selectStatus,
        sendVasGA,
        useButtonTextForPartnerFunction,
        getManiaCardData,
        isLoading,
        maniaRewards?.length,
        reward,
    ]);

    return (
        <WithSkeletonContext isLoading={isLoading}>
            <SliderHoc
                cardLength={getLengthCards()}
                needWrapGap={true}
                withoutArrows={withoutArrows}
            >
                {offerCards}
            </SliderHoc>
            {renderPopup && renderPopupMemoized()}
        </WithSkeletonContext>
    );
};

export default PromoContainer;
